import React from "react";
import {PathComponent} from "index";
import {BaseFragment, BaseFragmentProps, BaseFragmentState} from "@/shared/BaseFragment";
import {lightGray, translucentWhite} from "@/shared/colors";
import {StyledBoxRow, StyledSpan} from "@/shared/StyledComponents";
import {DIVIDER, PD_LG, PD_MD, PD_XLG, SZ_SM} from "@/shared/dimens";
import {ButtonBase, Card} from "@mui/material";
import {BaseApp} from "@/shared/BaseApp";
import {UserCache, UserProfilePhoto} from "@/shared/types";
import {UserFragment} from "@/shared/UserFragment";
import {App} from "./App";
import {getAuth} from "@firebase/auth";
import {User} from "@/shared/entities";
import {PracticeSelect} from "@/libwyze-shared/components/PracticeSelect";
import {AccountProfileContextTrait, Page, Pages} from "@/libwyze-shared/types";
import {DevicesFragment} from "./devices/DevicesFragment";
import {Tabs, TabsColorScheme, TabsContainer} from "@/shared/TabsContainer";
import {DevicesOutlined} from "@mui/icons-material";
import {AppTabsContainer} from "../shared/AppTabsContainer";

enum Tab {
  DEVICES = "devices",
}

const TABS: Tabs<Tab> = {
  items: [
    {
      type: Tab.DEVICES,
      path: "devices",
      text: "Devices",
      iconType: DevicesOutlined,
      render: path => <DevicesFragment path={path}/>,
    },
  ],
  titleElement: <StyledBoxRow style={{alignItems: "center"}}>
    <img src={"/images/dentwyze_vantage_logotype.svg"} style={{height: 48, margin: PD_MD}}/>
  </StyledBoxRow>,
  containerId: "main",
}

export type MainProps = BaseFragmentProps & {}

type MainState = BaseFragmentState & {
  user?: User,
  practicePage?: Page,
}

export class Main extends BaseFragment<MainProps, MainState> {

  private readonly auth = getAuth();

  static nestedPaths(): PathComponent[] {
    return TabsContainer.nestedPathsFromTabs(TABS);
  }

  protected async fetchOnMount(forceReload?: boolean): Promise<any> {
    const user = await UserCache.getInstance().getUser(this.auth.currentUser.uid);
    const practicePageId = BaseApp.CONTEXT.getTrait<AccountProfileContextTrait>().getPracticePageId();
    const practicePage = await Pages.getInstance().getOrLoadItem(practicePageId);
    this.setState({
      user,
      practicePage,
    });
  }

  protected renderContainerContent(): React.ReactElement | null {
    const tabsColorScheme: TabsColorScheme = "light";
    return <div
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        width: "100dvw",
        height: "100dvh",
        background: lightGray,
        overflow: "hidden",
      }}>
      <StyledBoxRow style={{
        position: "relative",
        zIndex: 1,
        background: translucentWhite,
        width: "100%",
        alignItems: "center",
        padding: `0px ${PD_XLG}px`,
        height: 72,
        flexShrink: 0,
        borderBottom: DIVIDER,
      }}>
        <img src={"/images/dentwyze_logotype.svg"} style={{height: 32}}/>
        <img src={"/images/dentwyze_checkin_logotype.svg"} style={{height: 40, marginRight: PD_LG}}/>
        <StyledSpan/>
        <PracticeSelect
          practices={[this.state.practicePage.practicePageProfile]}
          selectedPractice={this.state.practicePage.practicePageProfile}
          setSelectedPractice={null}
          linkPractice={null}/>
        <Card style={{marginLeft: PD_LG, width: SZ_SM, height: SZ_SM, flexShrink: 0}}>
          <ButtonBase
            onClick={() => App.CONTEXT.showDialog(
              null,
              () => <UserFragment uid={this.auth.currentUser.uid}/>)}
          >
            <img src={UserProfilePhoto(this.state.user)} style={{width: "100%", height: "100%"}}/>
          </ButtonBase>
        </Card>
      </StyledBoxRow>
      <AppTabsContainer
        path={this.props.path}
        noLogo={true}
        noUserButton={true}
        tabs={TABS}
        tabsThemeColor={this.theme.palette.grey[tabsColorScheme as string === "light" ? 100 : 900]}
        tabsColorScheme={tabsColorScheme}
      />
    </div>;
  }
}